import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getNotifications,
  markNotificationRead,
  markViewRead,
  type Inbox,
  type Notification,
  type NotificationLinkView,
} from '../api/notifications';
import { useAuth } from '../auth/AuthProvider';
import { Icon } from '../lib/icons';

/** How often the bell re-checks. Slow enough to be free, fast enough to feel live. */
const POLL_MS = 30_000;

const InboxContext = createContext<Inbox | null>(null);

/**
 * FEAT-014 — the notification state, fetched once for the whole shell.
 *
 * The bell and the sidebar badges read the same response. Fetching per consumer
 * would mean the badge and the bell could disagree about the same number, which
 * is worse than either being briefly stale.
 */
export function NotificationsProvider({ children }: { children: ReactNode }): ReactElement {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['notifications'],
    queryFn: getNotifications,
    // Only while signed in. Polling `/notifications` without a session produces
    // a 401 every thirty seconds and a refresh attempt behind each one.
    enabled: Boolean(user),
    refetchInterval: POLL_MS,
    staleTime: 0,
  });

  return <InboxContext.Provider value={query.data ?? null}>{children}</InboxContext.Provider>;
}

export function useInbox(): Inbox | null {
  return useContext(InboxContext);
}

/**
 * REQ-119 — opening a screen marks its notifications read.
 *
 * Mounted once in the shell rather than called by each page, so a screen cannot
 * be added without its badge clearing. The alternative — a hook every page
 * remembers to call — is a hook a page eventually forgets.
 */
export function useMarkViewReadOnVisit(): void {
  const location = useLocation();
  const inbox = useInbox();
  const queryClient = useQueryClient();

  /* The last path we cleared for, so re-renders do not re-post. */
  const cleared = useRef<string | null>(null);

  const view = viewForPath(location.pathname);
  const pending = view ? (inbox?.badges[view] ?? 0) : 0;

  useEffect(() => {
    if (!view || pending === 0) return;
    if (cleared.current === location.pathname) return;

    cleared.current = location.pathname;

    void markViewRead(view).then((next) => {
      queryClient.setQueryData(['notifications'], next);
    });
  }, [view, pending, location.pathname, queryClient]);

  // Leaving a screen re-arms it, so coming back later clears whatever arrived
  // in the meantime.
  useEffect(() => {
    if (cleared.current !== location.pathname) cleared.current = null;
  }, [location.pathname]);
}

function viewForPath(pathname: string): NotificationLinkView | null {
  if (pathname.startsWith('/support')) return 'support';
  if (pathname.startsWith('/bookings')) return 'bookings';
  if (pathname.startsWith('/visitors')) return 'visitors';
  if (pathname.startsWith('/employees')) return 'employees';
  return null;
}

/**
 * The bell, ported from the prototype's `renderTopbar`.
 *
 * Same markup and same classes — `.notif-wrap`, `.notif-panel`, `.np-item` —
 * because the vendored stylesheet already defines all of them. What the
 * prototype did not have is any of the semantics below: it was a `div` with a
 * click handler, so the panel was unreachable by keyboard and unannounced to a
 * screen reader.
 */
export function NotificationBell(): ReactElement {
  const { t } = useTranslation();
  const inbox = useInbox();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const items = inbox?.items ?? [];
  const unread = inbox?.unread ?? 0;

  const apply = useCallback(
    (next: Inbox) => queryClient.setQueryData(['notifications'], next),
    [queryClient],
  );

  const readAll = useMutation({
    mutationFn: () => markNotificationRead(),
    onSuccess: apply,
  });

  const readOne = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: apply,
  });

  /* Click outside and Escape both close it, as any disclosure should. */
  useEffect(() => {
    if (!isOpen) return;

    function onPointerDown(event: MouseEvent): void {
      if (!wrapRef.current?.contains(event.target as Node)) setIsOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  function open(notification: Notification): void {
    readOne.mutate(notification.id);
    setIsOpen(false);

    /*
     * The deep-link is a hint, not an entitlement: it navigates to the screen,
     * and the screen's own request is re-authorised server-side. A recipient who
     * has since lost access gets that screen's ordinary "not available" state
     * rather than anything the notification granted them.
     */
    if (notification.link_view) navigate(`/${notification.link_view}`);
  }

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        type="button"
        className="icon-btn"
        onClick={() => setIsOpen((value) => !value)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={
          unread > 0
            ? `${t('notifications')} — ${t('nt_unread', { count: unread })}`
            : t('notifications')
        }
      >
        <Icon name="bell" />
        {unread > 0 ? <span className="dot">{unread}</span> : null}
      </button>

      {isOpen ? (
        <div className="notif-panel open" role="dialog" aria-label={t('notifications')}>
          <div className="np-head">
            <span>{t('notifications')}</span>
            {items.length > 0 ? (
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => readAll.mutate()}
                disabled={readAll.isPending || unread === 0}
              >
                {t('mark_read')}
              </button>
            ) : null}
          </div>

          {/*
            `aria-live` so the count change is announced after "mark all read" —
            without it the panel silently empties and a screen-reader user has
            no confirmation the button did anything.
          */}
          <div className="np-list" aria-live="polite">
            {items.length === 0 ? (
              <div className="empty" style={{ padding: 26 }}>
                {t('no_notif')}
              </div>
            ) : (
              items.slice(0, 12).map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  className="np-item"
                  onClick={() => open(notification)}
                >
                  <span className={notification.read_at ? 'np-dot read' : 'np-dot'} />
                  <div>
                    <div className="np-tx">{notification.message}</div>
                    <div className="np-tm">
                      <bdi>{formatWhen(notification.occurred_at)}</bdi>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    timeZone: 'Asia/Riyadh',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
